"use client"
import { useState, useMemo, useEffect } from "react"
import {
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Input,
    Select,
    Switch,
    useDisclosure,
    useToast,
    FormControl,
    FormLabel,
    FormErrorMessage,
    NumberInput,
    NumberInputField,
    Textarea,
    VStack,
    HStack,
    Text,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    Flex,
    IconButton,
    Container,
    InputGroup,
    InputLeftElement,
    TableContainer,
    Checkbox,
} from "@chakra-ui/react"
import { AddIcon, ViewIcon, EditIcon, DeleteIcon, SearchIcon } from "@chakra-ui/icons"
import { useDispatch, useSelector } from "react-redux"
import {
    fetchSubscriptionEmployerTopUpAsync,
    addSubscriptionEmployerTopUpAsync,
    deleteSubscriptionEmployerTopUpAsync,
    selectSubscriptionEmployerTopUp,
    updateSubscriptionEmployerTopUpAsync
} from "../../Subscriptions/SubscriptionEmployerTopUp/SubscriptionEmployerTopUpSlice"

const initialTopUpState = {
    type: "employer",
    topup_type: "",
    value: "",
    mrp_amount: "",
    amount: "",
    sort_order: "",
    validity: "",
    validity_type: "day",
    status: "active",
}

// Define which top-up types should use a switch (boolean) input
const SWITCH_TYPES = {
    employer: [
        "profile_creation",
        "notification_via_email_sms_whatsapp",
        "smart_boost_via_email_sms_whatsapp",
        "dedicated_relationship_manager",
        "campion_via_email_sms_whatsapp",
        "multiple_user_login",
        "reports",
        "company_branding"
    ],
    candidate: [
        "notification_via_email_sms_whatsapp",
        "resume_builder",
        "profile_creation"
    ]
}

const AdminSubscriptionTopUp = () => {
    const dispatch = useDispatch()
    const topUps = useSelector(selectSubscriptionEmployerTopUp)
    const [currentTopUp, setCurrentTopUp] = useState(initialTopUpState)
    const [selectedTopUps, setSelectedTopUps] = useState([])
    const [searchTerm, setSearchTerm] = useState("")
    const [userTypeFilter, setUserTypeFilter] = useState("all")
    const [topUpTypeFilter, setTopUpTypeFilter] = useState("all")
    const [errors, setErrors] = useState({})
    const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure()
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
    const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure()
    const [isLoading, setIsLoading] = useState(true)
    const toast = useToast()

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true)
                await dispatch(fetchSubscriptionEmployerTopUpAsync()).unwrap()
            } catch (error) {
                toast({
                    title: "Failed to load subscription top-ups.",
                    description: error.toString(),
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                })
            } finally {
                setIsLoading(false)
            }
        }
        fetchData()
    }, [dispatch, toast])

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setCurrentTopUp({ ...currentTopUp, [name]: value })
        if (errors[name]) {
            setErrors({ ...errors, [name]: "" })
        }
    }

    const handleSwitchChange = (name, value) => {
        // For switch types, we store 1 or 0 in the value field
        setCurrentTopUp({ ...currentTopUp, [name]: value ? "1" : "0" })
    }

    const handleNumberInputChange = (name, value) => {
        setCurrentTopUp({ ...currentTopUp, [name]: value })
        if (errors[name]) {
            setErrors({ ...errors, [name]: '' })
        }
    }

    const validateForm = () => {
        const newErrors = {}
        if (!currentTopUp.type) newErrors.type = "User type is required."
        if (!currentTopUp.topup_type) newErrors.topup_type = "Top-up type is required."

        // Validate value field based on top-up type
        const isSwitchType = SWITCH_TYPES[currentTopUp.type]?.includes(currentTopUp.topup_type)
        if (!isSwitchType && (currentTopUp.value === "" || isNaN(parseFloat(currentTopUp.value)))) {
            newErrors.value = "Valid value is required."
        }

        if (currentTopUp.mrp_amount === "" || isNaN(parseFloat(currentTopUp.mrp_amount)))
            newErrors.mrp_amount = "Valid MRP amount is required."
        if (currentTopUp.amount === "" || isNaN(parseFloat(currentTopUp.amount)))
            newErrors.amount = "Valid amount is required."
        if (currentTopUp.sort_order === "" || isNaN(parseInt(currentTopUp.sort_order, 10)))
            newErrors.sort_order = "Valid sort order is required."
        if (currentTopUp.validity === "" || isNaN(parseInt(currentTopUp.validity, 10)))
            newErrors.validity = "Valid validity is required."

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const preparePayload = (topUpData) => {
        return {
            ...topUpData,
            value: SWITCH_TYPES[topUpData.type]?.includes(topUpData.topup_type)
                ? parseInt(topUpData.value || '0', 10)
                : parseInt(topUpData.value || '0', 10),
            mrp_amount: parseFloat(topUpData.mrp_amount || '0'),
            amount: parseFloat(topUpData.amount || '0'),
            sort_order: parseInt(topUpData.sort_order || '0', 10),
            validity: parseInt(topUpData.validity || '0', 10),
        }
    }

    const handleAddTopUp = async () => {
        if (!validateForm()) {
            toast({
                title: "Validation Error",
                description: "Please fill all required fields correctly.",
                status: "error",
                duration: 3000,
                isClosable: true,
            })
            return
        }

        try {
            const newTopUpPayload = preparePayload(currentTopUp)
            await dispatch(addSubscriptionEmployerTopUpAsync(newTopUpPayload)).unwrap()
            toast({
                title: "Top-up added successfully.",
                status: "success",
                duration: 2000,
                isClosable: true,
            })
            onAddClose()
            dispatch(fetchSubscriptionEmployerTopUpAsync())
        } catch (error) {
            toast({
                title: "Failed to add top-up.",
                description: error.toString(),
                status: "error",
                duration: 3000,
                isClosable: true,
            })
        }
    }

    const handleUpdateTopUp = async () => {
        if (!validateForm()) {
            toast({
                title: "Validation Error",
                description: "Please fill all required fields correctly.",
                status: "error",
                duration: 3000,
                isClosable: true,
            })
            return
        }

        try {
            const updatedTopUpPayload = preparePayload(currentTopUp)
            await dispatch(
                updateSubscriptionEmployerTopUpAsync({
                    id: currentTopUp.uuid,
                    updateSubscriptiondata: updatedTopUpPayload,
                }),
            ).unwrap()
            toast({
                title: "Top-up updated successfully.",
                status: "success",
                duration: 2000,
                isClosable: true,
            })
            onEditClose()
            dispatch(fetchSubscriptionEmployerTopUpAsync())
        } catch (error) {
            toast({
                title: "Failed to update top-up.",
                description: error.toString(),
                status: "error",
                duration: 3000,
                isClosable: true,
            })
        }
    }

    const handleDeleteTopUp = async () => {
        try {
            await dispatch(deleteSubscriptionEmployerTopUpAsync(currentTopUp.uuid)).unwrap()
            toast({
                title: "Top-up deleted successfully.",
                status: "success",
                duration: 2000,
                isClosable: true,
            })
            onDeleteClose()
            dispatch(fetchSubscriptionEmployerTopUpAsync())
        } catch (error) {
            toast({
                title: "Failed to delete top-up.",
                description: error.toString(),
                status: "error",
                duration: 3000,
                isClosable: true,
            })
        }
    }

    const handleEditTopUp = (topUp) => {
        const topUpForEditing = {
            ...initialTopUpState,
            ...topUp,
            value: String(topUp.value || ''),
            mrp_amount: String(topUp.mrp_amount || ''),
            amount: String(topUp.amount || ''),
            sort_order: String(topUp.sort_order || ''),
            validity: String(topUp.validity || ''),
        }
        setCurrentTopUp(topUpForEditing)
        setErrors({})
        onEditOpen()
    }

    const handleDeleteConfirmation = (topUpId) => {
        const topUpToDelete = topUps.find((topUp) => topUp.uuid === topUpId)
        if (topUpToDelete) {
            setCurrentTopUp(topUpToDelete)
            onDeleteOpen()
        }
    }

    const handleViewTopUp = (topUp) => {
        setCurrentTopUp(topUp)
        onViewOpen()
    }

    const filteredTopUps = useMemo(() => {
        if (!topUps || !Array.isArray(topUps) || topUps.length === 0) {
            return []
        }
        return topUps.filter((topUp) => {
            if (!topUp) return false
            const typeMatch = userTypeFilter === "all" || topUp.type === userTypeFilter
            const topUpTypeMatch = topUpTypeFilter === "all" || topUp.topup_type === topUpTypeFilter
            return typeMatch && topUpTypeMatch
        })
    }, [topUps, userTypeFilter, topUpTypeFilter])

    const toggleSelectAll = () => {
        if (selectedTopUps.length === filteredTopUps.length && filteredTopUps.length > 0) {
            setSelectedTopUps([])
        } else {
            setSelectedTopUps(filteredTopUps.map((topUp) => topUp.uuid))
        }
    }

    const toggleTopUpSelection = (topUpId) => {
        if (selectedTopUps.includes(topUpId)) {
            setSelectedTopUps(selectedTopUps.filter((id) => id !== topUpId))
        } else {
            setSelectedTopUps([...selectedTopUps, topUpId])
        }
    }

    const renderValueField = () => {
        const isSwitchType = SWITCH_TYPES[currentTopUp.type]?.includes(currentTopUp.topup_type)

        if (isSwitchType) {
            return (
                <FormControl>
                    <FormLabel>Value</FormLabel>
                    <Switch
                        isChecked={currentTopUp.value === "1"}
                        onChange={(e) => handleSwitchChange("value", e.target.checked)}
                    >
                        {currentTopUp.value === "1" ? "Enabled" : "Disabled"}
                    </Switch>
                </FormControl>
            )
        }

        return (
            <FormControl isInvalid={!!errors.value}>
                <FormLabel>Value</FormLabel>
                <NumberInput
                    min={0}
                    value={currentTopUp.value}
                    onChange={(valueAsString) => handleNumberInputChange("value", valueAsString)}
                >
                    <NumberInputField name="value" />
                </NumberInput>
                <FormErrorMessage>{errors.value}</FormErrorMessage>
            </FormControl>
        )
    }

    const renderTopUpForm = () => (
        <VStack spacing={4}>
            <FormControl isInvalid={!!errors.type}>
                <FormLabel>Type</FormLabel>
                <Select
                    name="type"
                    value={currentTopUp.type}
                    onChange={handleInputChange}
                >
                    <option value="employer">Employer</option>
                    <option value="candidate">Candidate</option>
                </Select>
                <FormErrorMessage>{errors.type}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.topup_type}>
                <FormLabel>Top-up Type</FormLabel>
                <Select
                    name="topup_type"
                    placeholder="Select top-up type"
                    value={currentTopUp.topup_type}
                    onChange={handleInputChange}
                >
                    {currentTopUp.type === "employer" ? (
                        <>
                            <option value="job_post">Job Post</option>
                            <option value="urgent_hiring_tag">Urgent Hiring Tag</option>
                            <option value="candidate_unlock">Candidate Unlock</option>
                            <option value="job_published">Job Published</option>
                            <option value="profile_creation">Profile Creation</option>
                            <option value="notification_via_email_sms_whatsapp">Email/SMS/WhatsApp Notification</option>
                            <option value="job_boosts">Job Boosts</option>
                            <option value="job_applicants_download">Job Applicants Download</option>
                            <option value="unlocked_candidate_download">Unlocked Candidate Download</option>
                            <option value="smart_boost_via_email_sms_whatsapp">Smart Boost</option>
                            <option value="dedicated_relationship_manager">Dedicated Relationship Manager</option>
                            <option value="campion_via_email_sms_whatsapp">Campaign via Email/SMS/WhatsApp</option>
                            <option value="multiple_user_login">Multiple User Login</option>
                            <option value="reports">Reports</option>
                            <option value="company_branding">Company Branding</option>
                        </>
                    ) : (
                        <>
                            <option value="resume_builder_apply">Resume Builder Apply</option>
                            <option value="job_apply">Job Apply</option>
                            <option value="notification_via_email_sms_whatsapp">Email/SMS/WhatsApp Notification</option>
                            <option value="resume_builder">Resume Builder</option>
                            <option value="profile_creation">Profile Creation</option>
                        </>
                    )}
                </Select>
                <FormErrorMessage>{errors.topup_type}</FormErrorMessage>
            </FormControl>

            {/* Dynamic value field */}
            {renderValueField()}

            {/* Common fields */}
            <HStack width="100%">
                <FormControl isInvalid={!!errors.mrp_amount}>
                    <FormLabel>MRP Amount</FormLabel>
                    <NumberInput
                        min={0}
                        value={currentTopUp.mrp_amount}
                        onChange={(valueAsString) => handleNumberInputChange("mrp_amount", valueAsString)}
                    >
                        <NumberInputField name="mrp_amount" />
                    </NumberInput>
                    <FormErrorMessage>{errors.mrp_amount}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors.amount}>
                    <FormLabel>Amount</FormLabel>
                    <NumberInput
                        min={0}
                        value={currentTopUp.amount}
                        onChange={(valueAsString) => handleNumberInputChange("amount", valueAsString)}
                    >
                        <NumberInputField name="amount" />
                    </NumberInput>
                    <FormErrorMessage>{errors.amount}</FormErrorMessage>
                </FormControl>
            </HStack>

            <FormControl isInvalid={!!errors.sort_order}>
                <FormLabel>Sort Order</FormLabel>
                <NumberInput
                    min={1}
                    value={currentTopUp.sort_order}
                    onChange={(valueAsString) => handleNumberInputChange("sort_order", valueAsString)}
                >
                    <NumberInputField name="sort_order" />
                </NumberInput>
                <FormErrorMessage>{errors.sort_order}</FormErrorMessage>
            </FormControl>

            <HStack width="100%">
                <FormControl isInvalid={!!errors.validity}>
                    <FormLabel>Validity</FormLabel>
                    <NumberInput
                        min={1}
                        value={currentTopUp.validity}
                        onChange={(valueAsString) => handleNumberInputChange("validity", valueAsString)}
                    >
                        <NumberInputField name="validity" />
                    </NumberInput>
                    <FormErrorMessage>{errors.validity}</FormErrorMessage>
                </FormControl>
                <FormControl>
                    <FormLabel>Validity Type</FormLabel>
                    <Select
                        name="validity_type"
                        value={currentTopUp.validity_type}
                        onChange={handleInputChange}
                    >
                        <option value="day">Day</option>
                        <option value="month">Month</option>
                        <option value="year">Year</option>
                    </Select>
                </FormControl>
            </HStack>

            <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                    name="status"
                    value={currentTopUp.status}
                    onChange={handleInputChange}
                >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                </Select>
            </FormControl>
        </VStack>
    )

    return (
        <div className="contentAdmin bg-adminbg p-4" style={{ fontFamily: "Inter, sans-serif" }}>
            <div className=" bg-white  my-5 rounded-md  ">
                <Container maxW="container.2xl" className="bg-white my-2 rounded-md">
                    <Flex justifyContent="space-between" alignItems="center" mb={6}>
                        <h2 className="text-xl text-black font-semibold px-3 py-7">Subscription Top-ups</h2>
                        <Button
                            leftIcon={<AddIcon />}
                            colorScheme="blue"
                            onClick={() => {
                                setCurrentTopUp(initialTopUpState)
                                setErrors({})
                                onAddOpen()
                            }}
                        >
                            Add New Top-up
                        </Button>
                    </Flex>
                    <VStack spacing={4} mb={8}>
                        <HStack width="100%" spacing={4}>
                            <InputGroup>
                                <InputLeftElement pointerEvents="none">
                                    <SearchIcon color="gray.300" />
                                </InputLeftElement>
                                <Input
                                    placeholder="Search top-ups..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </InputGroup>
                            <Select
                                value={userTypeFilter}
                                onChange={(e) => setUserTypeFilter(e.target.value)}
                            >
                                <option value="all">All User Types</option>
                                <option value="employer">Employer</option>
                                <option value="candidate">Candidate</option>
                            </Select>
                            <Select
                                value={topUpTypeFilter}
                                onChange={(e) => setTopUpTypeFilter(e.target.value)}
                            >
                                <option value="all">All Top-up Types</option>
                                {userTypeFilter === "employer" || userTypeFilter === "all" ? (
                                    <>
                                        <option value="job_post">Job Post</option>
                                        <option value="urgent_hiring_tag">Urgent Hiring Tag</option>
                                        <option value="candidate_unlock">Candidate Unlock</option>
                                        <option value="job_published">Job Published</option>
                                    </>
                                ) : null}
                                {userTypeFilter === "candidate" || userTypeFilter === "all" ? (
                                    <>
                                        <option value="resume_builder_apply">Resume Builder Apply</option>
                                        <option value="job_apply">Job Apply</option>
                                    </>
                                ) : null}
                            </Select>
                        </HStack>
                    </VStack>
                    <TableContainer>
                        <Table colorScheme="gray" size="sm">
                            <Thead className="bg-gray-200">
                                <Tr>
                                    <Th width="40px">
                                        <Checkbox
                                            isChecked={selectedTopUps.length === filteredTopUps.length && filteredTopUps.length > 0}
                                            onChange={toggleSelectAll}
                                            isDisabled={filteredTopUps.length === 0}
                                        />
                                    </Th>
                                    <Th>Type</Th>
                                    <Th>Top-up Type</Th>
                                    <Th>Value</Th>
                                    <Th>Amount</Th>
                                    <Th>Validity</Th>
                                    <Th>Status</Th>
                                    <Th>Actions</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {filteredTopUps.map((topUp) => (
                                    <Tr key={topUp.uuid}>
                                        <Td>
                                            <Checkbox
                                                isChecked={selectedTopUps.includes(topUp.uuid)}
                                                onChange={() => toggleTopUpSelection(topUp.uuid)}
                                            />
                                        </Td>
                                        <Td>
                                            <Badge colorScheme={topUp.type === "employer" ? "green" : "purple"}>
                                                {topUp.type}
                                            </Badge>
                                        </Td>
                                        <Td>{topUp.topup_type}</Td>
                                        <Td>
                                            {SWITCH_TYPES[topUp.type]?.includes(topUp.topup_type) ?
                                                (topUp.value === "1" ? "Enabled" : "Disabled") :
                                                topUp.value}
                                        </Td>
                                        <Td>
                                            ₹{topUp.amount}
                                            <Text as="span" fontSize="sm" color="gray.500" ml={2}>
                                                <s>₹{topUp.mrp_amount}</s>
                                            </Text>
                                        </Td>
                                        <Td>
                                            {topUp.validity} {topUp.validity_type}
                                            {topUp.validity != 1 ? "s" : ""}
                                        </Td>
                                        <Td>
                                            <Badge colorScheme={topUp.status === "active" ? "green" : "red"}>
                                                {topUp.status}
                                            </Badge>
                                        </Td>
                                        <Td>
                                            <HStack spacing={2}>
                                                <IconButton
                                                    aria-label="View top-up"
                                                    icon={<ViewIcon />}
                                                    size="sm"
                                                    colorScheme="blue"
                                                    variant="outline"
                                                    onClick={() => handleViewTopUp(topUp)}
                                                />
                                                <IconButton
                                                    aria-label="Edit top-up"
                                                    icon={<EditIcon />}
                                                    size="sm"
                                                    colorScheme="green"
                                                    variant="outline"
                                                    onClick={() => handleEditTopUp(topUp)}
                                                />
                                                <IconButton
                                                    aria-label="Delete top-up"
                                                    icon={<DeleteIcon />}
                                                    size="sm"
                                                    colorScheme="red"
                                                    variant="outline"
                                                    onClick={() => handleDeleteConfirmation(topUp.uuid)}
                                                />
                                            </HStack>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>

                    {/* Add/Edit Modals */}
                    <Modal isOpen={isAddOpen} onClose={onAddClose} size="5xl" isCentered>
                        <ModalOverlay />
                        <ModalContent maxHeight={"80vh"}>
                            <ModalHeader>Add New Top-up</ModalHeader>
                            <ModalCloseButton />
                            <ModalBody overflowY="auto">{renderTopUpForm()}</ModalBody>
                            <ModalFooter>
                                <Button variant="ghost" mr={3} onClick={onAddClose}>
                                    Cancel
                                </Button>
                                <Button colorScheme="blue" onClick={handleAddTopUp}>
                                    Add Top-up
                                </Button>
                            </ModalFooter>
                        </ModalContent>
                    </Modal>

                    <Modal isOpen={isEditOpen} onClose={onEditClose} size="5xl" isCentered>
                        <ModalOverlay />
                        <ModalContent maxHeight={"80vh"}>
                            <ModalHeader>Edit Top-up</ModalHeader>
                            <ModalCloseButton />
                            <ModalBody overflowY="auto">{renderTopUpForm()}</ModalBody>
                            <ModalFooter>
                                <Button variant="ghost" mr={3} onClick={onEditClose}>
                                    Cancel
                                </Button>
                                <Button colorScheme="blue" onClick={handleUpdateTopUp}>
                                    Update Top-up
                                </Button>
                            </ModalFooter>
                        </ModalContent>
                    </Modal>

                    {/* Delete Modal */}
                    <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>Delete Top-up</ModalHeader>
                            <ModalCloseButton />
                            <ModalBody>
                                Are you sure you want to delete the top-up "{currentTopUp.topup_type}" for {currentTopUp.type}?
                            </ModalBody>
                            <ModalFooter>
                                <Button variant="ghost" mr={3} onClick={onDeleteClose}>
                                    Cancel
                                </Button>
                                <Button colorScheme="red" onClick={handleDeleteTopUp}>
                                    Delete
                                </Button>
                            </ModalFooter>
                        </ModalContent>
                    </Modal>
                </Container>
            </div>
        </div>
    )
}

export default AdminSubscriptionTopUp